<i18n src="./resources/locales.json"></i18n>

<template>
  <section>
    <v-progress-linear
      v-if="isLoading"
      indeterminate
      height="3"
      color="var(--v-surface-base)"
      style="margin-bottom: -3px"
    />
    <div class="contact-form">
      <div v-for="item in crmTemplateFields" :key="item.standardFieldName">
        <v-text-field
          v-model="item.value"
          type="text"
          :counter="item.maxLength"
          :hide-details="!item.editable"
          :label="item.customFieldName"
          :maxlength="item.maxLength"
          :readonly="!item.editable"
          :rules="getRules(item)"
          :loading="loadingFields.includes(item.standardFieldName)"
          @change="updateItem(item)"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import DateTime from 'luxon/src/datetime';
import libPhoneNumber from 'libphonenumber-js';
import { CrmService } from '@/services';

export default {
  name: 'EcsContactForm',

  props: {
    contact: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      isLoading: false,
      loadingFields: [],
      lastUpdateValues: {}
    };
  },

  computed: {
    ...mapGetters(['getCrmTemplate']),

    crmTemplate() {
      return this.getCrmTemplate();
    },

    crmTemplateFields() {
      return !this.crmTemplate
        ? []
        : this.crmTemplate.displayTemplateFields.map(fieldTemplate => {
            return {
              ...fieldTemplate,
              value: this.contact[fieldTemplate.standardFieldName]
            };
          });
    },

    modifiedTime() {
      const time = DateTime.fromISO(this.contact.modifiedTime);
      return `${time.toLocaleString(DateTime.DATETIME_FULL)} &bull; ${time
        .toRelative()
        .replace(/^\\w/, c => c.toUpperCase())}`;
    }
  },

  created() {
    if (!this.crmTemplate) {
      this.isLoading = true;
      this.getCrmTemplates().finally(() => {
        this.isLoading = false;
      });
    }
  },

  methods: {
    ...mapActions(['getCrmTemplates', 'snackbarSuccessMessage', 'snackbarErrorMessage']),

    getRules(item) {
      const rules = [];
      const { isRequired, format, minLength, maxLength, minValue, maxValue } = item;

      if (isRequired) {
        rules.push(value => !!value || this.$t('validation.required'));
      }

      if (format) {
        const regex = new RegExp(format);
        rules.push(value => regex.test(value) || this.$t('validation.format'));
      }

      if (minLength) {
        if (isRequired) {
          rules.push(value => {
            return (value || '').length >= minLength || this.$t('validation.minLength', { minLength });
          });
        } else {
          rules.push(value => {
            const input = value || '';
            return (
              input.length === 0 ||
              input === '' ||
              input.length >= minLength ||
              this.$t('validation.minLength', { minLength })
            );
          });
        }
      }

      if (maxLength) {
        rules.push(value => {
          return (value || '').length <= maxLength || this.$t('validation.maxLength', { maxLength });
        });
      }

      if (minValue) {
        rules.push(value => value >= minValue || this.$t('validation.minValue', { minValue }));
      }

      if (maxValue) {
        rules.push(value => value <= maxValue || this.$t('validation.maxValue', { maxValue }));
      }

      if (this.isTelephonyEndPoint(item.standardFieldName)) {
        rules.push(value => this.isValidPhoneNumber(value) || this.$t('validation.invalidPhoneNumber'));
      }

      return rules;
    },

    isTelephonyEndPoint(propertyName = '') {
      return propertyName.toLowerCase().startsWith('phone');
    },

    isValidPhoneNumber(value = '') {
      if (!value || value.length === 0) {
        return true;
      }

      let phoneNumber;

      try {
        phoneNumber = libPhoneNumber.parsePhoneNumber(value.startsWith('+') ? value : `+1${value}`);
      } catch {
        return this.$t('validation.invalidPhoneNumber');
      }

      if (phoneNumber.isValid() && phoneNumber.country !== 'US') {
        return true;
      }

      if (!phoneNumber.isValid()) {
        return this.$t('validation.invalidPhoneNumber');
      }

      return true;
    },

    isValid(item) {
      const rules = this.getRules(item);
      return rules.every(rule => rule(item.value) === true);
    },

    updateItem(item) {
      if (!this.isValid(item)) return;
      if (this.lastUpdateValues[item.standardFieldName] === item.value) return;

      this.loadingFields.push(item.standardFieldName);
      this.lastUpdateValues[item.standardFieldName] = item.value;

      const contactInfo = {
        [item.standardFieldName]: item.value
      };

      const onUpdateSuccess = ({ data }) => {
        const response = data[0][item.standardFieldName];
        if (response.error) {
          this.snackbarErrorMessage(
            this.$t('messages.updateError', { field: item.standardFieldName, error: response.message })
          );
        } else {
          this.snackbarSuccessMessage(this.$t('messages.updateSuccess', { name: item.customFieldName }));
        }
      };

      const onUpdateError = () => {
        this.snackbarErrorMessage(this.$t('messages.updateError', { error: '' }));
      };

      return CrmService.updateContact(this.contact.id, contactInfo)
        .then(onUpdateSuccess)
        .catch(onUpdateError)
        .finally(() => {
          this.loadingFields = this.loadingFields.filter(field => field !== item.standardFieldName);
        });
    }
  }
};
</script>

<style scoped>
.contact-form {
  padding: 20px 80px 40px;
}
</style>
